import type {
  ActivityEvent,
  Annotation,
  AnnotationComment,
  Attachment,
  AttachmentThumbnail,
  TextureLink,
  Call,
  Channel,
  ChannelFolder,
  ChannelMember,
  ChecklistItem,
  Comment,
  EpochMs,
  Folder,
  InboxItem,
  KnowledgeDoc,
  Label,
  Mention,
  Message,
  Milestone,
  Presence,
  Project,
  Reaction,
  SavedMessage,
  StorageFolder,
  StorageSettings,
  Subscription,
  Task,
  TaskLink,
  TaskStatusDef,
  TaskType,
  Thread,
  TimeEntry,
  User,
  UserGroup,
  UserGroupMember,
  Workspace,
} from '@/mock/types';

export type ColumnKind = 'text' | 'int' | 'bigint' | 'bool' | 'json';

export interface ColumnSpec {
  readonly ts: string;
  readonly pg: string;
  readonly kind: ColumnKind;
  readonly nullable: boolean;
}

export interface TableSpec {
  readonly name: string;
  readonly pg: string;
  readonly collection: string;
  readonly primaryKey: readonly string[];
  readonly columns: readonly ColumnSpec[];
  readonly visibility: 'all' | 'own' | 'channel' | 'journal' | 'personalFiles';
  readonly ownerColumn?: string;
  readonly channelColumn?: string;
}

type KindOf<T> = [NonNullable<T>] extends [string]
  ? 'text'
  : [NonNullable<T>] extends [EpochMs]
    ? 'bigint'
    : [NonNullable<T>] extends [number]
      ? 'int'
      : [NonNullable<T>] extends [boolean]
        ? 'bool'
        : 'json';

interface ColumnShape<T> {
  readonly kind: KindOf<T>;
  readonly nullable: null extends T ? true : false;
}

type ColumnMap<Row> = { readonly [K in keyof Row & string]-?: ColumnShape<Row[K]> };

type Field<Row> = keyof Row & string;

type TableDefinition<Row> = {
  readonly name: string;
  readonly collection: string;
  readonly primaryKey: readonly Field<Row>[];
  readonly columns: ColumnMap<Row>;
} & (
  | { readonly visibility: 'all' }
  | { readonly visibility: 'own'; readonly ownerColumn: Field<Row> }
  | { readonly visibility: 'journal'; readonly ownerColumn: Field<Row> }
  | { readonly visibility: 'personalFiles'; readonly ownerColumn: Field<Row> }
  | { readonly visibility: 'channel'; readonly channelColumn: Field<Row> }
);

const text = { kind: 'text', nullable: false } as const;
const textOrNull = { kind: 'text', nullable: true } as const;
const int = { kind: 'int', nullable: false } as const;
const intOrNull = { kind: 'int', nullable: true } as const;
const moment = { kind: 'bigint', nullable: false } as const;
const momentOrNull = { kind: 'bigint', nullable: true } as const;
const flag = { kind: 'bool', nullable: false } as const;
const flagOrNull = { kind: 'bool', nullable: true } as const;
const json = { kind: 'json', nullable: false } as const;
const jsonOrNull = { kind: 'json', nullable: true } as const;

export function snakeCase(name: string): string {
  return name.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function defineTable<Row>(definition: TableDefinition<Row>): TableSpec {
  const { columns, ...rest } = definition;
  const shapes = columns as unknown as Record<string, { kind: ColumnKind; nullable: boolean }>;
  return {
    ...rest,
    pg: snakeCase(definition.name),
    columns: Object.entries(shapes).map(([ts, shape]) => ({
      ts,
      pg: snakeCase(ts),
      kind: shape.kind,
      nullable: shape.nullable,
    })),
  };
}

export const TABLES: readonly TableSpec[] = [
  defineTable<User>({
    name: 'subject',
    collection: 'users',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, kind: text, role: text, displayName: text, handle: text,
      avatarUrl: textOrNull, initials: text, accentColor: text, jobTitle: textOrNull,
      email: textOrNull, about: textOrNull, aboutJson: jsonOrNull, contacts: json,
      birthdayOn: textOrNull, startedOn: textOrNull, timezone: text, workHours: json,
      active: flag,
    },
  }),
  defineTable<Presence>({
    name: 'presence',
    collection: 'presence',
    primaryKey: ['userId'],
    visibility: 'all',
    columns: {
      userId: text, state: text, statusEmoji: textOrNull, statusText: textOrNull,
      statusUntilAt: momentOrNull, lastSeenAt: moment, typingInChannelId: textOrNull,
    },
  }),
  defineTable<Workspace>({
    name: 'workspace',
    collection: 'workspaces',
    primaryKey: ['id'],
    visibility: 'all',
    columns: { id: text, name: text, icon: text, color: text, memberIds: json },
  }),
  defineTable<Folder>({
    name: 'folder',
    collection: 'folders',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, workspaceId: text, parentId: textOrNull, name: text, icon: text, color: text,
      sortKey: text, archivedAt: momentOrNull, createdAt: moment, updatedAt: moment,
    },
  }),
  defineTable<Project>({
    name: 'project',
    collection: 'projects',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, workspaceId: text, folderId: textOrNull, key: text, name: text, icon: text,
      color: text, description: textOrNull, statusIds: json, typeIds: json, memberIds: json,
      startOn: textOrNull, targetOn: textOrNull, nextTaskNumber: int, sortKey: text,
      archivedAt: momentOrNull, createdAt: moment, updatedAt: moment,
    },
  }),
  defineTable<Milestone>({
    name: 'milestone',
    collection: 'milestones',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, workspaceId: text, projectId: text, name: text, icon: text, description: text,
      color: text, dueOn: textOrNull, completedAt: momentOrNull, sortKey: text,
      archivedAt: momentOrNull, createdAt: moment, updatedAt: moment,
    },
  }),
  defineTable<Label>({
    name: 'label',
    collection: 'labels',
    primaryKey: ['id'],
    visibility: 'all',
    columns: { id: text, workspaceId: text, name: text, color: text, createdBy: textOrNull, archivedAt: momentOrNull },
  }),
  defineTable<TaskType>({
    name: 'taskType',
    collection: 'taskTypes',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, workspaceId: text, name: text, icon: text, color: text, sortKey: text,
      archivedAt: momentOrNull,
    },
  }),
  defineTable<TaskStatusDef>({
    name: 'taskStatus',
    collection: 'statuses',
    primaryKey: ['id'],
    visibility: 'all',
    columns: { id: text, name: text, color: text, category: text, sortKey: text },
  }),
  defineTable<Task>({
    name: 'task',
    collection: 'tasks',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, key: text, workspaceId: text, projectId: text, parentId: textOrNull, depth: int,
      typeId: textOrNull, milestoneId: textOrNull, title: text, descriptionJson: json,
      descriptionText: text, status: text, priority: text, assigneeId: textOrNull,
      creatorId: text, labelIds: json, estimateMinutes: intOrNull, startOn: textOrNull,
      dueOn: textOrNull, sortKey: text, createdAt: moment, updatedAt: moment,
      completedAt: momentOrNull, deletedAt: momentOrNull,
    },
  }),
  defineTable<ChecklistItem>({
    name: 'checklistItem',
    collection: 'checklist',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, taskId: text, text: text, assigneeId: textOrNull, creatorId: text, done: flag,
      doneAt: momentOrNull, sortKey: text, deletedAt: momentOrNull, createdAt: moment,
      updatedAt: moment,
    },
  }),
  defineTable<Comment>({
    name: 'comment',
    collection: 'comments',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, taskId: text, authorId: text, bodyJson: json, bodyText: text,
      replyToId: textOrNull, anchor: jsonOrNull, assigneeId: textOrNull,
      resolvedAt: momentOrNull, editedAt: momentOrNull, deletedAt: momentOrNull,
      createdAt: moment,
    },
  }),
  defineTable<TaskLink>({
    name: 'taskLink',
    collection: 'links',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, kind: text, fromTaskId: text, toTaskId: text, creatorId: text, createdAt: moment,
    },
  }),
  defineTable<TimeEntry>({
    name: 'timeEntry',
    collection: 'timeEntries',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, taskId: text, userId: text, spentOn: text, minutes: int, note: text,
      createdAt: moment,
    },
  }),
  defineTable<Subscription>({
    name: 'subscription',
    collection: 'subscriptions',
    primaryKey: ['userId', 'targetKind', 'targetId'],
    visibility: 'all',
    columns: {
      userId: text, targetKind: text, targetId: text, state: text, createdAt: moment,
      updatedAt: moment,
    },
  }),
  defineTable<ChannelFolder>({
    name: 'channelFolder',
    collection: 'channelFolders',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, workspaceId: text, name: text, icon: text, color: text, sortKey: text,
      archivedAt: momentOrNull, createdAt: moment,
    },
  }),
  defineTable<SavedMessage>({
    name: 'savedMessage',
    collection: 'savedMessages',
    primaryKey: ['userId', 'messageId'],
    visibility: 'own',
    ownerColumn: 'userId',
    columns: { userId: text, messageId: text, savedAt: moment },
  }),
  defineTable<Channel>({
    name: 'channel',
    collection: 'channels',
    primaryKey: ['id'],
    visibility: 'channel',
    channelColumn: 'id',
    columns: {
      id: text, workspaceId: text, kind: text, folderId: textOrNull, closed: flag,
      name: textOrNull, icon: text, topic: text, dmKey: textOrNull, creatorId: text,
      nextSeq: int, lastMessageAt: momentOrNull, lastMessageSeq: intOrNull, sortKey: text,
      archivedAt: momentOrNull, createdAt: moment,
    },
  }),
  defineTable<ChannelMember>({
    name: 'channelMember',
    collection: 'members',
    primaryKey: ['channelId', 'userId'],
    visibility: 'channel',
    channelColumn: 'channelId',
    columns: {
      channelId: text, userId: text, role: text, lastReadSeq: int, unreadCount: int,
      mentionCount: int, muted: flag, joinedAt: moment,
    },
  }),
  defineTable<Message>({
    name: 'message',
    collection: 'messages',
    primaryKey: ['id'],
    visibility: 'channel',
    channelColumn: 'channelId',
    columns: {
      id: text, channelId: text, seq: int, authorId: text, bodyJson: json, bodyText: text,
      threadRootId: textOrNull, replyToId: textOrNull, sendAt: momentOrNull, published: flag,
      pinned: flag, editedAt: momentOrNull, deletedAt: momentOrNull, createdAt: moment,
      systemKind: textOrNull,
    },
  }),
  defineTable<Thread>({
    name: 'thread',
    collection: 'threads',
    primaryKey: ['rootMessageId'],
    visibility: 'channel',
    channelColumn: 'channelId',
    columns: {
      rootMessageId: text, channelId: text, replyCount: int, participantIds: json,
      lastReplyAt: moment, followerIds: json,
    },
  }),
  defineTable<Call>({
    name: 'call',
    collection: 'calls',
    primaryKey: ['id'],
    visibility: 'channel',
    channelColumn: 'channelId',
    columns: {
      id: text, channelId: text, rootMessageId: text, startedBy: text, startedAt: moment,
      endedAt: momentOrNull, participants: json, attendance: json,
    },
  }),
  defineTable<KnowledgeDoc>({
    name: 'knowledgeDoc',
    collection: 'docs',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, workspaceId: text, parentId: textOrNull, title: text, bodyJson: json,
      bodyText: text, ownerId: text, creatorId: text, editLocked: flag, shareToken: textOrNull,
      coverAttachmentId: textOrNull, sortKey: text, archivedAt: momentOrNull, createdAt: moment, updatedAt: moment,
    },
  }),
  defineTable<Reaction>({
    name: 'reaction',
    collection: 'reactions',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, ownerKind: text, ownerId: text, userId: text, emoji: text, createdAt: moment,
    },
  }),
  defineTable<Attachment>({
    name: 'attachment',
    collection: 'attachments',
    primaryKey: ['id'],
    visibility: 'personalFiles',
    ownerColumn: 'uploaderId',
    columns: {
      id: text, ownerKind: text, ownerId: text, name: text, mime: text, sizeBytes: int,
      url: text, width: intOrNull, height: intOrNull, kind: text, status: text, uploaderId: text,
      createdAt: moment, folderId: textOrNull,
    },
  }),
  defineTable<StorageFolder>({
    name: 'storageFolder',
    collection: 'storageFolders',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, workspaceId: text, parentId: textOrNull, name: text, sortKey: text,
      createdAt: moment, createdBy: text,
    },
  }),
  defineTable<UserGroup>({
    name: 'userGroup',
    collection: 'userGroups',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, workspaceId: text, name: text, icon: textOrNull, color: textOrNull, createdBy: text,
      createdAt: moment, sortKey: text, archivedAt: momentOrNull,
    },
  }),
  defineTable<UserGroupMember>({
    name: 'userGroupMember',
    collection: 'userGroupMembers',
    primaryKey: ['groupId', 'userId'],
    visibility: 'all',
    columns: { groupId: text, userId: text, addedBy: text, addedAt: moment },
  }),
  defineTable<Mention>({
    name: 'mention',
    collection: 'mentions',
    primaryKey: ['sourceKind', 'sourceId', 'targetKind', 'targetId'],
    visibility: 'all',
    columns: {
      sourceKind: text, sourceId: text, targetKind: text, targetId: text, createdAt: moment,
    },
  }),
  defineTable<InboxItem>({
    name: 'inboxItem',
    collection: 'inbox',
    primaryKey: ['id'],
    visibility: 'own',
    ownerColumn: 'userId',
    columns: {
      id: text, seq: int, userId: text, kind: text, actorId: text, sourceKind: text,
      sourceId: text, taskId: textOrNull, channelId: textOrNull, state: text, preview: text,
      createdAt: moment, stateChangedAt: moment, snoozedUntilAt: momentOrNull,
    },
  }),
  defineTable<ActivityEvent>({
    name: 'activityEvent',
    collection: 'activity',
    primaryKey: ['id'],
    visibility: 'journal',
    ownerColumn: 'actorId',
    columns: {
      id: text, seq: int, at: moment, actorId: text, actorKind: text, mutator: text,
      entityTable: text, entityId: text, op: text, before: jsonOrNull, after: jsonOrNull,
      reversible: flag, batchId: textOrNull, taskIds: jsonOrNull,
    },
  }),
  defineTable<StorageSettings>({
    name: 'storageSettings',
    collection: 'storageSettings',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, endpoint: textOrNull, publicEndpoint: textOrNull, region: textOrNull,
      bucket: textOrNull, accessKeyId: textOrNull, pathStyle: flagOrNull,
      maxAttachmentBytes: intOrNull, updatedAt: momentOrNull, updatedBy: textOrNull,
    },
  }),
  defineTable<Annotation>({
    name: 'annotation',
    collection: 'annotations',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, attachmentId: text, authorId: text, anchor: json, createdAt: moment,
      resolvedAt: momentOrNull, resolvedBy: textOrNull,
    },
  }),
  defineTable<AnnotationComment>({
    name: 'annotationComment',
    collection: 'annotationComments',
    primaryKey: ['id'],
    visibility: 'all',
    columns: {
      id: text, annotationId: text, authorId: text, bodyJson: json, bodyText: text, createdAt: moment,
    },
  }),
  defineTable<AttachmentThumbnail>({
    name: 'attachmentThumbnail',
    collection: 'attachmentThumbnails',
    primaryKey: ['attachmentId'],
    visibility: 'all',
    columns: {
      attachmentId: text, url: text, createdAt: moment,
    },
  }),
  defineTable<TextureLink>({
    name: 'textureLink',
    collection: 'textureLinks',
    primaryKey: ['modelAttachmentId', 'name'],
    visibility: 'all',
    columns: { modelAttachmentId: text, name: text, textureAttachmentId: text },
  }),
];

const BY_NAME = new Map(TABLES.map((spec) => [spec.name, spec]));
const BY_COLLECTION = new Map(TABLES.map((spec) => [spec.collection, spec]));

export function tableByName(name: string): TableSpec {
  const spec = BY_NAME.get(name);
  if (spec === undefined) {
    throw new Error(`Неизвестная таблица «${name}»`);
  }
  return spec;
}

export function tableByCollection(collection: string): TableSpec {
  const spec = BY_COLLECTION.get(collection);
  if (spec === undefined) {
    throw new Error(`Неизвестная коллекция «${collection}»`);
  }
  return spec;
}
